/**
 * The health probes (PLAN §10.2's status path, §11.1): what `GET /health`
 * answers on the cloud API and on a node. Unauthenticated, cheap, and NOT in
 * the route table — they are not tenant data, they answer before a caller
 * could have a token (the connectivity panel probes them before and during
 * sign-in), and the cloud serves `/health` on its own unauthenticated API
 * Gateway route (`infrastructure/api-http.tf`) in front of the router.
 *
 *     cloud:  { service: 'cloud-api', stage, version, time, api }
 *     node:   { service: 'state-service', status, stage, version, time,
 *               tenantId, siteId, nodeId, label, storeId, schemaVersion,
 *               cloudLink, outboxDepth, api }
 *
 * `api` (D37 §4.7): `{ level, features }` — what the host's REST API offers
 * (`clientIdentity.ts`); absent from a host older than it.
 *
 * `version` is what is running: the cloud API's bundle hash (set by OpenTofu
 * from the Lambda zip), a node's package hash (D15; `ACP_VERSION`, `dev`
 * until packaging lands). `time` is the server's clock (ISO), so a reader can
 * show drift.
 *
 * `cloudLink` is the node's own view of its site link to the cloud (PLAN
 * §10.2's "Node→cloud" column): `not-configured` until the site link exists
 * (phase 4), then `connected` / `disconnected`. `outboxDepth` is how many
 * applied patches wait in the node's outbox for that link.
 *
 * The producers answer these strict schemas (their tests parse what they
 * answer). The READERS — the CLI's `acp status`, the SPA's probes — parse
 * `anyHealthWireSchema`, built from the `.loose()` variants: they talk to servers
 * of other versions (PLAN §4.3), and a newer server adding a field must not
 * read as unreachable.
 */
import { z } from 'zod';
import { healthApiWireSchema } from './clientIdentity.js';
import { releaseRejectionReasonWireSchema } from './nodeUpdate.js';
import { pairNodeRoleWireSchema, pairRoleWireSchema, witnessInForceWireSchema, witnessKindWireSchema } from './witness.js';
import { boundedIdWireSchema, sha256HexWireSchema, cloudLinkStateReadWireSchema, cloudLinkStateWireSchema, isoTimestampWireSchema, peerLinkStateWireSchema } from './primitives.js';

export const cloudHealthWireSchema = z
  .object({
    service: z.literal('cloud-api'),
    stage: z.string().min(1),
    version: z.string().min(1),
    time: isoTimestampWireSchema,
    /** What the API offers (D37 §4.7, `clientIdentity.ts`): a client feature-detects against it. Absent from an older cloud: none. */
    api: healthApiWireSchema.optional(),
  })
  .strict();

/**
 * Something about the node's cloud identity an operator must act on: today only `revoked` — the cloud replaced or
 * removed this node, so its site link is stopped (docs/sitelink.md). `message` says what to do.
 */
const cloudIdentityNoticeWireSchema = z
  .object({
    status: z.literal('revoked'),
    since: isoTimestampWireSchema,
    message: z.string().min(1),
  })
  .strict();
export type CloudIdentityNoticeWire = z.infer<typeof cloudIdentityNoticeWireSchema>;

/**
 * A production this node runs that the cloud does not know as one of the node's site (docs/sitelink.md, "A production
 * the cloud refuses"): its record was refused (`PUT /node/productions/{id}`), its studio is not one of the site's in
 * the cloud, or the cloud refused its playout frames. The node keeps running it locally and stops sending its state
 * up; `fix` is the way out.
 */
export const cloudRefusalWireSchema = z
  .object({
    productionId: boundedIdWireSchema,
    reason: z.string().min(1),
    since: isoTimestampWireSchema,
    fix: z.string().min(1),
  })
  .strict();
export type CloudRefusalWire = z.infer<typeof cloudRefusalWireSchema>;

/**
 * A node's main/backup pair as it sees it (PLAN §8.6: the connectivity panel
 * "shows which node is active per production"): the peer link, the
 * witnesses, and each production's role here. Absent on a node without a peer.
 */
const nodeRedundancyHealthWireSchema = z
  .object({
    /** D21's pairing: main or backup (roles are per production; this is the node's place in the site). */
    nodeRole: pairNodeRoleWireSchema,
    peer: z
      .object({
        nodeId: boundedIdWireSchema,
        url: z.string().min(1),
        /** The peer link (its long poll answered within the lease window). */
        link: peerLinkStateWireSchema,
        lastContactAgoMs: z.number().int().min(0).nullable(),
      })
      .strict(),
    witnesses: z.array(
      z
        .object({ url: z.string().min(1), kind: witnessKindWireSchema, reachable: z.boolean(), lastContactAgoMs: z.number().int().min(0).nullable() })
        .strict(),
    ),
    /** Which witness confirms failovers and renews the lease here, and the expected failover time (wave 4c). */
    witness: witnessInForceWireSchema.optional(),
    productions: z.array(
      z
        .object({
          productionId: boundedIdWireSchema,
          /** This node's process: active, standby, or none running here. */
          role: pairRoleWireSchema.nullable(),
          term: z.number().int().min(0),
          activeNode: boundedIdWireSchema.nullable(),
          alarm: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type NodeRedundancyHealthWire = z.infer<typeof nodeRedundancyHealthWireSchema>;

/** A release id, as `/health` reports it (the site link's `hello.release` shape: `siteLink.ts`). */
const nodeHealthReleaseWireSchema = z.object({ listHash: sha256HexWireSchema, commit: z.string().min(1).max(64) }).strict();

/**
 * The node's update state (D40): a release installed but held back by the on-air guard (`waiting`: which, and how many
 * productions hold it), and the releases it would not keep (`rejected`: the supervisor's rollbacks and the janitor's
 * refusals, newest last).
 */
const nodeHealthUpdateWireSchema = z
  .object({
    waiting: z
      .object({ listHash: sha256HexWireSchema, commit: z.string().min(1).max(64), productionsActive: z.number().int().min(0), since: isoTimestampWireSchema })
      .strict()
      .nullable(),
    rejected: z.array(
      z
        .object({ listHash: sha256HexWireSchema.nullable(), reason: releaseRejectionReasonWireSchema, detail: z.string().max(2000), at: isoTimestampWireSchema })
        .strict(),
    ),
  })
  .strict();
export type NodeHealthUpdateWire = z.infer<typeof nodeHealthUpdateWireSchema>;

export const nodeHealthWireSchema = z
  .object({
    service: z.literal('state-service'),
    /** Liveness, kept from the first shape of this endpoint. */
    status: z.literal('ok'),
    /** The cloud stage this node belongs to (`ACP_STAGE`), or `local` for a node with none configured. */
    stage: z.string().min(1),
    version: z.string().min(1),
    time: isoTimestampWireSchema,
    tenantId: boundedIdWireSchema,
    siteId: boundedIdWireSchema,
    nodeId: boundedIdWireSchema,
    /** What operators call this node (`ACP_NODE_LABEL`, default the node id). */
    label: z.string().min(1),
    storeId: z.string().min(1),
    schemaVersion: z.number().int(),
    cloudLink: cloudLinkStateWireSchema,
    outboxDepth: z.number().int().min(0),
    /** The main/backup pair (phase 5), on a node with a peer. */
    redundancy: nodeRedundancyHealthWireSchema.optional(),
    /** Set when the cloud revoked this node (its link is stopped): what happened and what to do. */
    cloudIdentity: cloudIdentityNoticeWireSchema.optional(),
    /** Set when the node's LAN IP is one its peer cannot reach (`lanIpProblem`, `sites.ts`): why, and the ways out. */
    lanWarning: z.string().min(1).optional(),
    /** Set while this node runs productions the cloud does not know as its site's (`cloudRefusalWireSchema`). */
    cloudRefusals: z.array(cloudRefusalWireSchema).optional(),
    /** What the node's API offers (D37 §4.7, `clientIdentity.ts`). Absent from an older node: none — a newer SPA hides what it lacks. */
    api: healthApiWireSchema.optional(),
    /**
     * The release the node runs (D37 §4.1: as its site-link `hello` says), or null when it names none (a dev build,
     * mid hot swap). A page the node serves names its own build by it (`x-acp-client: web-node/<commit>`), so the web
     * package embeds no commit and stays byte-identical while its source is unchanged (D40). Absent from an older node.
     */
    release: nodeHealthReleaseWireSchema.nullable().optional(),
    /** Its update state (D40: `nodeHealthUpdateWireSchema`), on a node the supervisor runs. Absent from an older node, and outside the container. */
    update: nodeHealthUpdateWireSchema.optional(),
  })
  .strict();

/**
 * What a reader accepts: either service, unknown extra fields kept, and the
 * closed values opened up — a newer node reporting `status: 'degraded'` or a
 * cloud link state this reader does not know must read as REACHABLE, not as
 * "not an ACP health report" (which would fail a working primary over). A
 * link state it does not know is, as far as it can tell, not connected.
 */
export const anyHealthWireSchema = z.discriminatedUnion('service', [
  // An `api` this reader cannot read is no negotiation at all (it then assumes the host offers nothing new), never unreachable.
  cloudHealthWireSchema.extend({ api: healthApiWireSchema.loose().optional().catch(undefined) }).loose(),
  // A notice this reader cannot read is dropped, not a reason to call the node unreachable.
  nodeHealthWireSchema
    .extend({
      status: z.string().min(1),
      cloudLink: cloudLinkStateReadWireSchema,
      cloudIdentity: cloudIdentityNoticeWireSchema.optional().catch(undefined),
      cloudRefusals: z.array(cloudRefusalWireSchema.loose()).optional().catch(undefined),
      api: healthApiWireSchema.loose().optional().catch(undefined),
      release: nodeHealthReleaseWireSchema.loose().nullable().optional().catch(undefined),
      update: nodeHealthUpdateWireSchema.loose().optional().catch(undefined),
    })
    .loose(),
]);

export type CloudHealthWire = z.infer<typeof cloudHealthWireSchema>;
export type NodeHealthWire = z.infer<typeof nodeHealthWireSchema>;
export type AnyHealthWire = z.infer<typeof anyHealthWireSchema>;

/** Where every host answers its health probe, relative to its API base URL. */
export const HEALTH_PATH = '/health';
